<template>
  <section
    class="widget-upcoming-view"
  >
    <header class="widget-header">
      <h2
        class="widget-header__title"
      >
        <span class="widget-header__title-accent">{{ titleParts.lead }}</span><span class="widget-header__title-plain">{{ titleParts.rest }}</span>
      </h2>
      <div class="widget-header__actions">
        <span
          class="widget-header__meta-label"
        >
          {{ $t('widget.nextDraws') }}
        </span>
        <a
          v-if="showAllLink"
          class="widget-all-chip"
          href="#"
          @click.prevent="handleViewAll"
        >
          {{ $t('widget.seeAll') }}
        </a>
      </div>
    </header>

    <div
      v-if="loading"
      class="widget-state"
    >
      {{ $t('common.loading') }}
    </div>

    <div
      v-else-if="error"
      class="widget-state widget-state--error"
    >
      {{ error }}
    </div>

    <div
      v-else-if="draws.length === 0"
      class="widget-state"
    >
      {{ $t('widget.noUpcomingDraws') }}
    </div>

    <div
      v-else
      class="widget-carousel"
    >
      <div
        ref="carouselRef"
        class="widget-track"
        @scroll.passive="updateScrollState"
      >
        <WidgetLotteryCard
          v-for="draw in draws"
          :key="`${draw.game_slug}-${draw.id}`"
          :lottery="draw"
          @remove="handleRemove"
        />
      </div>
      <button
        v-if="canScrollPrev"
        type="button"
        class="widget-scroll-arrow widget-scroll-arrow--previous"
        :aria-label="$t('common.previous')"
        @click="scrollPrev"
      >
        <svg
          viewBox="0 0 24 24"
          aria-hidden="true"
        ><path d="m15 6-6 6 6 6" /></svg>
      </button>
      <button
        v-if="canScrollNext"
        type="button"
        class="widget-scroll-arrow widget-scroll-arrow--next"
        :aria-label="$t('common.next')"
        @click="scrollNext"
      >
        <svg
          viewBox="0 0 24 24"
          aria-hidden="true"
        ><path d="m9 6 6 6-6 6" /></svg>
      </button>
    </div>
  </section>
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import WidgetLotteryCard from '@/components/cards/WidgetLotteryCard.vue'
import { useUpcomingDrawsStore } from '@/stores/upcomingDraws'
import { useAuthStore } from '@/stores/auth'
import { initializeTheme } from '@/composables/useTheme'
import type { UpcomingDraw } from '@/types/lottery'
import { iframeCommunication } from '@/utils/iframeCommunication'
import '@/styles/widget.css'

const route = useRoute()
const { t } = useI18n()
const authStore = useAuthStore()

const titleParts = computed(() => {
  const raw = sectionTitle.value.trim()
  const spaceIndex = raw.indexOf(' ')
  if (spaceIndex === -1) {
    return { lead: raw, rest: '' }
  }
  return {
    lead: raw.slice(0, spaceIndex),
    rest: raw.slice(spaceIndex),
  }
})

const carouselRef = ref<HTMLElement | null>(null)
const canScrollPrev = ref(false)
const canScrollNext = ref(false)

const updateScrollState = () => {
  const track = carouselRef.value
  canScrollPrev.value = !!track && track.scrollLeft > 1
  canScrollNext.value = !!track && track.scrollLeft + track.clientWidth < track.scrollWidth - 1
}
const upcomingDrawsStore = useUpcomingDrawsStore()

const draws = computed(() => upcomingDrawsStore.draws)

const showAllLink = computed(() => {
  const raw = Array.isArray(route.query.showAll) ? route.query.showAll[0] : route.query.showAll
  if (typeof raw === 'string') {
    return raw.trim().toLowerCase() !== 'false'
  }
  return true
})

const sectionTitle = computed(() => {
  const raw = Array.isArray(route.query.title) ? route.query.title[0] : route.query.title
  if (typeof raw === 'string' && raw.trim()) {
    return raw.trim()
  }
  return t('sections.luckyNumbers')
})

const loading = computed(() => upcomingDrawsStore.loading)
const error = computed(() => upcomingDrawsStore.error)

const getScrollStep = () => {
  if (!carouselRef.value) return 166
  const firstItem = carouselRef.value.firstElementChild as HTMLElement | null
  if (!firstItem) return 166
  const styles = window.getComputedStyle(carouselRef.value)
  const gapValue = parseFloat(styles.columnGap || styles.gap || '0')
  return firstItem.getBoundingClientRect().width + gapValue
}

const scrollPrev = () => {
  carouselRef.value?.scrollBy({ left: -getScrollStep(), behavior: 'smooth' })
}

const scrollNext = () => {
  carouselRef.value?.scrollBy({ left: getScrollStep(), behavior: 'smooth' })
}

const handleRemove = (draw: UpcomingDraw) => {
  const idx = upcomingDrawsStore.draws.findIndex((item) => item.id === draw.id)
  if (idx !== -1) {
    upcomingDrawsStore.draws.splice(idx, 1)
    if (upcomingDrawsStore.draws.length === 0) {
      void upcomingDrawsStore.fetchDraws(1)
    }
  }
  nextTick(() => {
    iframeCommunication.sendImmediateHeightUpdate()
  })
}

const handleViewAll = () => {
  iframeCommunication.sendMessage('widget-view-all', { view: 'all' })
}

const bootstrapWidgetContext = async () => {
  const account = route.query.account
  if (typeof account === 'string' && account.trim()) {
    authStore.account = account.trim()
    sessionStorage.setItem('auth_account', account.trim())
  }

  const currency = route.query.currency
  if (typeof currency === 'string' && currency.trim()) {
    sessionStorage.setItem('auth_currency', currency.split('?')[0])
  }

  try {
    await initializeTheme(true)
  } catch (themeError) {
    console.warn('Widget theme initialization failed:', themeError)
  }
}

let resizeObserver: ResizeObserver | null = null

watch(carouselRef, (track) => {
  resizeObserver?.disconnect()
  updateScrollState()
  if (track && typeof ResizeObserver !== 'undefined') {
    resizeObserver = new ResizeObserver(() => {
      updateScrollState()
      iframeCommunication.sendImmediateHeightUpdate()
    })
    resizeObserver.observe(track)
  }
}, { flush: 'post' })

const refetchDrawsAfterHandshake = () => {
  void upcomingDrawsStore.fetchDraws(1, undefined, true)
}

let stopParentReadyListener: (() => void) | null = null

onMounted(async () => {
  await bootstrapWidgetContext()

  authStore.refreshAuthFromExternalSources()
  stopParentReadyListener = iframeCommunication.on('parent-ready', refetchDrawsAfterHandshake)
  await upcomingDrawsStore.fetchDraws(1)

  await nextTick()

  iframeCommunication.startObserving('iframe-root')
  iframeCommunication.sendImmediateHeightUpdate()
})

onUnmounted(() => {
  resizeObserver?.disconnect()
  stopParentReadyListener?.()
  stopParentReadyListener = null
})

watch(
  () => route.query.account,
  async (account) => {
    if (typeof account === 'string' && account.trim()) {
      authStore.account = account.trim()
      sessionStorage.setItem('auth_account', account.trim())
      try {
        await initializeTheme(true)
      } catch (themeError) {
        console.warn('Widget theme refresh failed:', themeError)
      }
    }
  }
)

watch(
  draws,
  () => {
    nextTick(() => {
      updateScrollState()
      iframeCommunication.sendImmediateHeightUpdate()
    })
  },
  { deep: true }
)
</script>
